import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { noop } from 'lodash'
import { IconWarningNew, TooltipRenderer, Box, Text } from '@machobear/design-system'
import { fontWeights, colors, breakpoints } from '@/styles'

const Label = styled.div<{ isDisabled: boolean; fontSize: string; isLocked: boolean }>`
  font-weight: ${fontWeights.medium};
  font-size: ${props => props.fontSize};
  line-height: 20px;
  color: ${props => (props.isDisabled ? colors.grey56 : colors.link)};
  margin: 0;
  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  display: flex;
  gap: 8px;
  align-items: center;
  width: max-content;
  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 14px;
    line-height: 20px;
  }

  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`

const getSizeStyles = (size: string) => {
  if (size === 'small') return '14px'
  return '16px'
}

type DocumentViewProps = {
  src: string
  text?: string
  onClick?: () => void
  isDisabled?: boolean
  size?: string
  isLocked?: boolean
  documentStatus: 'verified' | 'removed' | 'pending' | 'standalone'
}

const DocumentView: React.FC<DocumentViewProps> = ({
  onClick = noop,
  text = '',
  isDisabled = false,
  size = 'medium',
  isLocked = false,
  documentStatus
}) => {
  const { t } = useTranslation()
  const randomWords = '***** ******** *****'
  const isStandalone = documentStatus === 'standalone'
  const displayText = text ? text : t('l.application.view.view_document.label', 'View Document')

  const handleClick = () => {
    if (!isDisabled && !isLocked) {
      onClick()
    }
  }

  const tooltips = {
    removed: t(
      'l.application.status.removed.label',
      "This document has either been deleted by the renter, or the liv.rent team has removed it as it didn't meet our verification requirements."
    ),
    pending: t(
      'l.application.view.document_pending.label',
      'This document is pending verification by the liv.rent team. Please check back in 1-3 business days.'
    ),
    standalone: t(
      'l.standalone.view.document_standalone.label',
      'No documents are provided because the tenant did not apply on liv.rent.'
    ),
    verified: null
  }

  const tooltip = tooltips?.[documentStatus]

  return tooltip ? (
    <TooltipRenderer
      id="viewDocumentTooltip"
      tooltip={
        <Box>
          <Text weight="regular" size="small">
            {tooltip}
          </Text>
        </Box>
      }
    >
      {({ triggerProps }) => (
        <Label
          fontSize={getSizeStyles(size)}
          isLocked={isLocked}
          isDisabled={isDisabled}
          {...triggerProps}
        >
          {isLocked ? randomWords : displayText}
          {isStandalone && <IconWarningNew tone="caution" />}
        </Label>
      )}
    </TooltipRenderer>
  ) : (
    <Label
      onClick={handleClick}
      isDisabled={isDisabled}
      fontSize={getSizeStyles(size)}
      isLocked={isLocked}
    >
      {isLocked ? randomWords : displayText}
    </Label>
  )
}

export default DocumentView
